import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import {
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent, DragOverEvent, DragMoveEvent } from '@dnd-kit/core'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '../../../lib/dataSource'
import type { Task, Section, TaskPlacement } from '../../../lib/db/models'
import { rememberUndo } from '../../../lib/undo'
import { EMPTY_SECTION_PREFIX } from './useSectionDrag'
import { createNestTracker, findNestCandidate, type NestTarget, type NestTracker, type Point } from '../utils/nestTarget'
import { checklistSlotAt, lineAt, ordersAtLine, type LineSlot } from '../utils/listSlot'
import type { SubtaskDragOut } from '../utils/subtaskDragOut'
import { landGhost } from '../utils/landGhost'
import { setPendingSubtaskSelection } from '../utils/pendingSubtaskSelection'
import { showInfoToast } from '../../../components/common/MessageToast'
import { dropPopMotion } from '../../../lib/motion/dropPopMotion'

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

// Dasselbe Objekt bei jedem Rendern (#303): `useSensor` merkt sich die Optionen nach Identität; ein frisches Objekt gab
// dem `DndContext` neue Sensoren, und jede Zeile mit `useSortable` renderte bei jedem Pfeil neu.
const POINTER_ACTIVATION = { activationConstraint: { distance: 5 } }

export type OptimisticUpdates = Map<string, { order: number; sectionId?: string | null }> | null

interface UseTaskDragAndDropParams {
  segments: RenderSegment[]
  tasks: Task[] | undefined
  sections: Section[] | undefined
  taskListRef: React.RefObject<HTMLDivElement | null>
  navSelectTask: (taskId: string) => void
  setOptimisticUpdates: Dispatch<SetStateAction<OptimisticUpdates>>
  /** When false, drags that would move a task into a different segment are rejected.
   *  Used by virtual-section pages (e.g. Quick Notes) where segments are computed
   *  from task metadata and can't be changed by reordering. */
  allowCrossSegmentDrag?: boolean
  /**
   * Klappt eine Aufgabe auf — das Ziel beim **Einhängen** (#204), damit man die neue
   * Unteraufgabe dort sieht; nach `⌘Z` eines **Umhängens** wieder die Quelle.
   */
  onExpandTask?: (taskId: string) => void
  /** **Herauslösen** (#204): Die neuen Aufgaben sollen angefasst in der Liste stehen, sobald sie dort sind. */
  onSelectTasks?: (taskIds: string[]) => void
}

/** Solange fährt die alte Stelle einer eingehängten Zeile zu (`TaskList.scss`, 260 ms), plus ein Bild. */
const LEAVE_MS = 300

export function useTaskDragAndDrop({
  segments,
  tasks,
  sections,
  taskListRef,
  navSelectTask,
  setOptimisticUpdates,
  allowCrossSegmentDrag = true,
  onExpandTask,
  onSelectTasks,
}: UseTaskDragAndDropParams) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const source = useDataSource()
  const { t } = useTranslation()
  const [draggedIds, setDraggedIds] = useState<Set<string> | null>(null)
  const [dragCount, setDragCount] = useState(0)
  const [isCrossSegmentDrag, setIsCrossSegmentDrag] = useState(false)
  // Index of the segment after which a drop-gap should appear (section header shifts down).
  const [gapAfterSegmentIdx, setGapAfterSegmentIdx] = useState<number | null>(null)
  // Height of the gap (matches the over item's height + 2px list gap)
  const gapHeightRef = useRef(0)

  // Pointer Y position — updated via passive pointermove listener.
  // Used to determine whether the gap opens before or after the hovered item.
  const pointerYRef = useRef(0)
  const pointerXRef = useRef(0)
  useEffect(() => {
    const handler = (e: PointerEvent) => { pointerYRef.current = e.clientY; pointerXRef.current = e.clientX }
    document.addEventListener('pointermove', handler, { passive: true })
    return () => document.removeEventListener('pointermove', handler)
  }, [])

  // Cross-segment gap direction: true = gap after over-item, false = gap before.
  // null = not yet determined (first handleDragOver sets it without dead zone).
  // Read by the sorting strategy in useDndComposition to decide displacement direction,
  // and by handleDragEnd to decide insertAfter.
  // The ref provides instant reads (no render delay) for the strategy function.
  // The state mirror triggers re-renders so SortableContext picks up direction changes
  // even when other state values (isCrossSegmentDrag, gapAfterSegmentIdx) stay the same.
  const crossSegGapAfterRef = useRef<boolean | null>(null)
  const [crossSegGapAfter, setCrossSegGapAfter] = useState<boolean | null>(null)

  // Ref mirrors draggedIds so handleDragEnd always reads the latest value,
  // regardless of stale closures or render timing.
  const draggedIdsRef = useRef<Set<string> | null>(null)

  // Pending IDs: set in handleDragStart, flushed to draggedIds state by useEffect.
  // This decouples the collapse animation from the first render where dnd-kit
  // measures activeNodeRect via useIsomorphicLayoutEffect. Without this, group
  // items collapse in the same render → the active item shifts up → dnd-kit
  // captures the wrong position → DragOverlay appears at item 1's position
  // instead of the grabbed item's position.
  const pendingDragIdsRef = useRef<Set<string> | null>(null)

  /*
    **Eingehängte Zeilen bleiben zu, bis die Liste sie nicht mehr führt** (gemeldet 28.09.2026).
    Vorher gab ein fester Timer (400 ms nach dem Schreiben) sie frei. Kam die Liste langsamer
    nach, stand die Zeile ein paar Bilder lang wieder voll da — oben in der Liste und zugleich
    in der Karte — und verschwand dann von einem Bild aufs nächste; die offene Karte ruckte mit.
  */
  const tasksRef = useRef(tasks)
  tasksRef.current = tasks
  // Ihre Stelle fährt nach dem Loslassen zu (`TaskList`, 260 ms): So lange hält die Liste die
  // Zeile auch dann, wenn die Datenbank schneller war — sonst spränge alles darunter.
  const leavingRef = useRef<{ ids: ReadonlySet<string>; until: number; giveUpAt: number } | null>(null)
  const leavingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const releaseLeaving = useCallback(() => {
    if (leavingTimerRef.current) clearTimeout(leavingTimerRef.current)
    leavingTimerRef.current = null
    if (!leavingRef.current) return
    leavingRef.current = null
    setDraggedIds(null)
  }, [])
  const checkLeaving = useCallback(() => {
    const leaving = leavingRef.current
    if (!leaving) return
    if (leavingTimerRef.current) clearTimeout(leavingTimerRef.current)
    const now = Date.now()
    const listed = (tasksRef.current ?? []).some(task => leaving.ids.has(task.id))
    // Führt eine Seite eingehängte Aufgaben weiter (oder kommt die Liste nie nach), bliebe die
    // Zeile sonst für immer zugeklappt.
    if (now >= leaving.giveUpAt || (!listed && now >= leaving.until)) return releaseLeaving()
    leavingTimerRef.current = setTimeout(checkLeaving, (listed ? leaving.giveUpAt : leaving.until) - now)
  }, [releaseLeaving])
  const holdUntilGone = useCallback((ids: readonly string[], droppedAt: number) => {
    leavingRef.current = { ids: new Set(ids), until: droppedAt + LEAVE_MS, giveUpAt: droppedAt + 3000 }
    checkLeaving()
  }, [checkLeaving])
  useEffect(() => { checkLeaving() }, [tasks, checkLeaving])
  useEffect(() => () => {
    if (leavingTimerRef.current) clearTimeout(leavingTimerRef.current)
  }, [])
  /**
   * **Einhängen per Tastatur und Menü** (⇧⌥↑/↓, „Einhängen in …“): Die Zeilen verlassen die
   * Liste wie beim Ziehen. Aufrufen im selben Moment, in dem das Ziel aufklappt, und VOR dem
   * Schreiben — nimmt die Datenbank sie vorher aus der Liste, ist keine Zeile mehr da, die
   * zufahren könnte (gemeldet 28.09.2026: Sie stand voll da und verschwand dann schlagartig).
   *
   * @returns nimmt es zurück, wenn die Datenbank ablehnt
   */
  const leaveList = useCallback((ids: readonly string[]) => {
    const all = [...(leavingRef.current?.ids ?? []), ...ids]
    setDraggedIds(new Set(all))
    holdUntilGone(all, Date.now())
    return releaseLeaving
  }, [holdUntilGone, releaseLeaving])

  /*
    **Einhängen per Ziehen** (#204). Das Ziel wird erst nach dem Verweilen auf der Mitte
    einer Zeile scharf (`utils/nestTarget`). Dann hält `nestLatchRef` das `over` von dnd-kit
    fest (siehe `useDndComposition`): Die anderen Zeilen bleiben stehen, statt Platz zu machen.
  */
  const [nestTarget, setNestTarget] = useState<NestTarget | null>(null)
  /** Die Einfügelinie — beim Herausziehen in der Liste, beim Einhängen in der offenen Checkliste. */
  const [insertLine, setInsertLine] = useState<{ top: number; left: number; width: number } | null>(null)
  /** Über der offenen Checkliste (Grill 11): wohin eingehängt wird. */
  const checklistSlotRef = useRef<{ parentId: string; afterId: string | null } | null>(null)
  const nestLatchRef = useRef<{ overId: string } | null>(null)
  const lastOverIdRef = useRef<string | null>(null)
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments
  // Was kein Ziel sein darf: beim Ziehen aus der Liste die gezogenen Aufgaben, beim
  // Herausziehen das eigene Elternteil. Und ob sich eine Gezogene wiederholt — dann ist eine
  // wiederkehrende Aufgabe kein Ziel (#205).
  const nestExcludeRef = useRef<ReadonlySet<string>>(new Set())
  const nestSourceRecurringRef = useRef(false)
  const targetRecurs = (id: string) => (tasksRef.current ?? []).some(task => task.id === id && task.recurrence != null)
  const nestTrackerRef = useRef<NestTracker | null>(null)
  if (!nestTrackerRef.current) {
    nestTrackerRef.current = createNestTracker({
      candidateAt: pointer => {
        const wrapper = taskListRef.current
        if (!wrapper) return null
        const moving = nestExcludeRef.current
        const open = new Set(segmentsRef.current.flatMap(seg => seg.tasks)
          .filter(task => task.status !== 'completed' && task.parentTaskId == null && !moving.has(task.id))
          .map(task => task.id))
        const rows = [...wrapper.querySelectorAll<HTMLElement>('[data-task-id]')]
          .filter(el => open.has(el.dataset.taskId ?? ''))
          .map(el => {
            const r = el.getBoundingClientRect()
            return { id: el.dataset.taskId!, top: r.top, bottom: r.bottom, left: r.left, right: r.right }
          })
        const hit = findNestCandidate(pointer, rows)
        return hit?.middle ? hit.id : null
      },
      blocked: targetId => nestSourceRecurringRef.current && targetRecurs(targetId),
      onChange: target => {
        nestLatchRef.current = target && !target.blocked && lastOverIdRef.current ? { overId: lastOverIdRef.current } : null
        setNestTarget(target)
      },
    })
  }
  const nestTracker = nestTrackerRef.current

  // Only PointerSensor — no KeyboardSensor.
  // dnd-kit's KeyboardSensor uses Enter/Space as drag-start keys, which
  // conflicts with our custom keyboard navigation (useProjectNavigation)
  // where Enter opens editing and Space toggles completion.
  const sensors = useSensors(
    useSensor(PointerSensor, POINTER_ACTIVATION),
  )

  // Optimistic updates are cleared by the page (`useOptimisticOrder`) once Dexie confirms them.

  // FLIP drop animation: after state clears, all dragged items appear at their
  // final list positions. This useLayoutEffect (before paint) starts a WAAPI
  // animation from the overlay's last position to each item's real position,
  // creating a "magnetic settle" effect. All items animate as one group —
  // they start stacked at the overlay position and fan out to their targets.
  // WAAPI runs on the compositor thread and doesn't conflict with React's
  // inline style management.
  // Auf dem Weg poppt die Zeile kurz hoch und sinkt ab (`dropPopMotion`, #238) — auch am
  // alten Platz, deshalb gibt es keine Mindeststrecke mehr.
  const dropAnimRef = useRef<{ ids: string[]; x: number; y: number } | null>(null)

  useLayoutEffect(() => {
    const anim = dropAnimRef.current
    if (!anim) return
    if (activeDragId) return // Only run after drag ends
    dropAnimRef.current = null

    const wrapper = taskListRef.current
    if (!wrapper) return

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
    for (const id of anim.ids) {
      const el = wrapper.querySelector(`[data-task-id="${id}"]`) as HTMLElement
      if (!el) continue

      const finalRect = el.getBoundingClientRect()
      const { motion, shadow, duration } = dropPopMotion({ dx: anim.x - finalRect.left, dy: anim.y - finalRect.top, reduced })
      const timing: KeyframeAnimationOptions = { duration, easing: 'linear', fill: 'none' }
      el.animate(motion, timing)
      // Der Schatten an die Pille, nicht an die eckige Hülle (gemeldet 03.10.2026).
      ;(el.querySelector<HTMLElement>('.task-entry') ?? el).animate(shadow, timing)
    }
  }, [activeDragId, taskListRef])

  // Deferred collapse: flush pendingDragIdsRef to state AFTER the first paint.
  // useEffect runs after paint, guaranteeing dnd-kit has already measured
  // activeNodeRect at the grabbed item's original position (before collapse).
  useEffect(() => {
    if (activeDragId && pendingDragIdsRef.current && !draggedIds) {
      const ids = pendingDragIdsRef.current
      pendingDragIdsRef.current = null
      setDraggedIds(ids)
    }
  }, [activeDragId, draggedIds])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeId = String(event.active.id)
    releaseLeaving()

    // Check which items are currently selected via the DOM (--selected BEM modifier).
    const selectedEls = taskListRef.current?.querySelectorAll('[data-task-id]') ?? []
    const selectedIds = new Set<string>()
    selectedEls.forEach((el: Element) => {
      if (el.querySelector('.task-entry--selected')) {
        const id = el.getAttribute('data-task-id')
        if (id) selectedIds.add(id)
      }
    })

    let ids: Set<string>
    if (selectedIds.has(activeId) && selectedIds.size > 1) {
      ids = selectedIds
    } else {
      if (!selectedIds.has(activeId)) {
        navSelectTask(activeId)
      }
      ids = new Set([activeId])
    }

    // Store in ref for handleDragEnd (immune to stale closures).
    draggedIdsRef.current = ids
    nestExcludeRef.current = ids
    nestSourceRecurringRef.current = (tasksRef.current ?? []).some(task => ids.has(task.id) && task.recurrence != null)
    // Set count immediately so DragOverlay renders the correct stack from frame 1.
    setDragCount(ids.size)
    // Trigger the overlay — dnd-kit measures activeNodeRect in useLayoutEffect.
    setActiveDragId(activeId)
    // Defer setDraggedIds to useEffect (fires AFTER paint). This ensures dnd-kit
    // measures the active item at its ORIGINAL position before group items collapse.
    pendingDragIdsRef.current = ids
  }, [navSelectTask, taskListRef, releaseLeaving])

  // Shared gap direction computation — called from handleDragOver (on over-item
  // change) and handleDragMove (on every pointer move). onDragOver only fires
  // when the over-item changes, but the pointer can cross the midpoint within
  // the same over-item. Without handleDragMove, the gap direction locks to
  // whatever it was when the item first became "over".
  const updateGapDirection = useCallback((overId: string, overSeg: number, overEl: HTMLElement | null) => {
    if (!overEl) return
    const overSegTasks = segments[overSeg]?.tasks
    if (!overSegTasks) return
    const isOverFirst = overId === overSegTasks[0]?.id
    const isOverLast = overId === overSegTasks[overSegTasks.length - 1]?.id

    const rect = overEl.getBoundingClientRect()
    const mid = rect.top + rect.height / 2
    const pointerY = pointerYRef.current
    const prev = crossSegGapAfterRef.current

    if (pointerY < rect.top) {
      // Pointer above the over-item → always gap before (first position reachable)
      crossSegGapAfterRef.current = false
    } else if (pointerY > rect.bottom) {
      // Pointer below the over-item → always gap after (last position reachable)
      crossSegGapAfterRef.current = true
    } else if (prev === null) {
      // First evaluation: set directly without dead zone
      crossSegGapAfterRef.current = pointerY >= mid
    } else if (prev && pointerY < mid - (isOverFirst ? 0 : 5)) {
      // Flip to gap-before. For the first item in a segment, no dead zone —
      // makes "first position" much easier to reach from the section header area.
      crossSegGapAfterRef.current = false
    } else if (!prev && pointerY > mid + (isOverLast ? 0 : 5)) {
      // Flip to gap-after. For the last item in a segment, no dead zone —
      // makes "last position" much easier to reach.
      crossSegGapAfterRef.current = true
    }
    // Mirror ref → state so SortableContext re-renders on direction change
    if (crossSegGapAfterRef.current !== prev) {
      setCrossSegGapAfter(crossSegGapAfterRef.current)
    }
  }, [segments])

  // Continuous gap direction tracking during drag. onDragMove fires on every
  // coordinate change (~60fps), unlike onDragOver which only fires when the
  // over-item changes. This ensures the gap direction updates when the pointer
  // crosses the midpoint of the current over-item.
  /*
    **Die offene Checkliste ist ein Ziel wie die Liste** (#204, Grill 11): Über ihr steht
    sofort eine Linie zwischen den Unteraufgaben, ohne Verweilen. Die Liste hält solange
    still (`nestLatchRef`), damit die Karte nicht unter dem Zeiger wegrückt.
  */
  const trackChecklist = useCallback((pointer: Point): boolean => {
    const wrapper = taskListRef.current
    const moving = draggedIdsRef.current ?? new Set<string>()
    // Nur die Checkliste der AUFGEKLAPPTEN Karte: Eine einmal geöffnete behält ihre Liste
    // zugeklappt im Baum (unsichtbar, über den Zeilen darunter) — sie zeigte sonst die Linie.
    const list = [...(wrapper?.querySelectorAll<HTMLElement>('.task-entry--expanded .subtask-list') ?? [])].find(el => {
      const r = el.getBoundingClientRect()
      return pointer.y >= r.top && pointer.y <= r.bottom && pointer.x >= r.left && pointer.x <= r.right
    })
    const parentId = list?.closest<HTMLElement>('[data-task-id]')?.dataset.taskId
    // Wiederkehrend in wiederkehrend gibt es nicht (#205): Diese Checkliste ist dann kein Ziel.
    const blocked = parentId != null && nestSourceRecurringRef.current && targetRecurs(parentId)
    if (!list || !parentId || moving.has(parentId) || blocked) {
      if (checklistSlotRef.current) {
        checklistSlotRef.current = null
        nestLatchRef.current = null
        setInsertLine(null)
      }
      return false
    }
    const rect = (el: Element, id: string) => {
      const r = el.getBoundingClientRect()
      return { id, top: r.top, bottom: r.bottom, left: r.left, right: r.right }
    }
    const rows = [...list.querySelectorAll<HTMLElement>('[data-subtask-id]')].map(el => rect(el, el.dataset.subtaskId!))
    const slot = checklistSlotAt(pointer, rows, rect(list, 'list'))
    if (!checklistSlotRef.current && lastOverIdRef.current) nestLatchRef.current = { overId: lastOverIdRef.current }
    checklistSlotRef.current = { parentId, afterId: slot.afterId }
    nestTracker.reset()
    setInsertLine(slot.line)
    return true
  }, [taskListRef, nestTracker])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const { over } = event
    // Nach dem Loslassen kann dnd-kit noch eine Bewegung nachreichen (ein Effekt auf den Zugweg);
    // sie zeichnete die Linie neu, und niemand nahm sie mehr weg (gemeldet 27.09.2026).
    if (draggedIdsRef.current) {
      if (!nestLatchRef.current) lastOverIdRef.current = over ? String(over.id) : null
      const pointer = { x: pointerXRef.current, y: pointerYRef.current }
      if (trackChecklist(pointer)) return
      nestTracker.update(pointer)
    }
    if (!over || !isCrossSegmentDrag) return
    const overId = String(over.id)
    if (overId.startsWith(EMPTY_SECTION_PREFIX)) return

    const overSeg = segments.findIndex(seg => seg.tasks.some(t => t.id === overId))
    if (overSeg === -1) return

    const overEl = document.querySelector(`[data-task-id="${overId}"]`) as HTMLElement
    updateGapDirection(overId, overSeg, overEl)
  }, [segments, isCrossSegmentDrag, updateGapDirection, nestTracker, trackChecklist])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event
    if (!over) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      return
    }
    const activeId = String(active.id)
    const overId = String(over.id)

    // Cross-segment drags are blocked for virtual-section pages (Quick Notes).
    // Ignore empty section drop zones and over-items in other segments entirely.
    if (!allowCrossSegmentDrag && overId.startsWith(EMPTY_SECTION_PREFIX)) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      return
    }

    // Hovering over an empty section drop zone — always cross-segment
    if (overId.startsWith(EMPTY_SECTION_PREFIX)) {
      setIsCrossSegmentDrag(true)
      setGapAfterSegmentIdx(null)
      return
    }

    const activeSeg = segments.findIndex(seg => seg.tasks.some(t => t.id === activeId))
    const overSeg = segments.findIndex(seg => seg.tasks.some(t => t.id === overId))
    const isCross = activeSeg !== -1 && overSeg !== -1 && activeSeg !== overSeg

    // Block cross-segment drags entirely when the flag is off — treat as no-op.
    if (isCross && !allowCrossSegmentDrag) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      return
    }

    if (!isCross) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      // Do NOT reset crossSegGapAfterRef here. When dragging from section 1
      // upward toward section 0, the pointer passes through other items in
      // section 1 first (same-segment). Resetting the ref here would erase the
      // gap direction, and re-entering cross-segment always re-initializes to
      // "gap after" (pointer below midpoint when approaching from below).
      // The ref is only meaningful during cross-segment drag and is reset
      // in handleDragStart/handleDragEnd/handleDragCancel.
      return
    }

    // ── Cross-segment drag (unified for both from-above and from-below) ──

    // 1. Determine gap direction via shared helper (also called from handleDragMove)
    const overEl = document.querySelector(`[data-task-id="${overId}"]`) as HTMLElement
    updateGapDirection(overId, overSeg, overEl)

    // 2. Source always collapses during cross-segment drag
    setIsCrossSegmentDrag(true)
    const gapAfter = crossSegGapAfterRef.current ?? true
    const itemHeight = overEl ? overEl.offsetHeight + 2 : 38
    const overSegTasks = segments[overSeg].tasks
    const isOverLast = overId === overSegTasks[overSegTasks.length - 1]?.id

    if (gapAfter && isOverLast) {
      // Last item + gap after: displacement can't shift anything.
      // CSS padding on the next segment IS the drop zone.
      gapHeightRef.current = itemHeight
      setGapAfterSegmentIdx(overSeg)
    } else if (activeSeg > overSeg) {
      // From below: padding after target compensates for source collapse
      gapHeightRef.current = itemHeight
      setGapAfterSegmentIdx(overSeg)
    } else {
      // From above: padding after source compensates for source collapse
      const activeEl = document.querySelector(`[data-task-id="${activeId}"]`) as HTMLElement
      gapHeightRef.current = activeEl ? activeEl.offsetHeight + 2 : 38
      setGapAfterSegmentIdx(activeSeg)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segments, allowCrossSegmentDrag])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    const droppedAt = Date.now()
    // Read from ref for guaranteed latest value (immune to stale closures).
    const currentDraggedIds = draggedIdsRef.current
    draggedIdsRef.current = null
    pendingDragIdsRef.current = null
    const nest = nestTracker.current
    const intoChecklist = checklistSlotRef.current
    checklistSlotRef.current = null
    setInsertLine(null)
    nestTracker.reset()
    nestLatchRef.current = null
    lastOverIdRef.current = null

    if (intoChecklist && tasks) {
      // Grill 11: an der Linie in die offene Checkliste — die Karte ist schon auf.
      const movingIds = currentDraggedIds ?? new Set([String(active.id)])
      const ids = tasks.filter(task => movingIds.has(task.id)).sort((a, b) => a.order - b.order).map(task => task.id)
      const first = tasks.find(task => task.id === ids[0])
      const stopLanding = landGhost(document.querySelector('.drag-overlay-lift'), ids, id => `[data-subtask-id="${id}"]`)
      setPendingSubtaskSelection(ids)
      crossSegGapAfterRef.current = null
      setCrossSegGapAfter(null)
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      setDraggedIds(movingIds)
      setDragCount(0)
      setActiveDragId(null)
      // Die Auswahl der Liste geht an die offene Karte (aufgeklappt zeigt sie keine). Bliebe
      // sie an der Gezogenen, rückte sie beim Verschwinden auf die nächste Aufgabe weiter —
      // zwei Auswahlen zugleich (gemeldet 27.09.2026).
      onExpandTask?.(intoChecklist.parentId)
      void source.nestTasks(ids, intoChecklist.parentId, intoChecklist.afterId).then(outcome => {
        if ('refused' in outcome) {
          // Ein Ast, der unter der Aufgabe tiefer als vier Ebenen reichte (#181): sagen, warum nichts geschah.
          if (outcome.refused === 'depth') showInfoToast(t('taskList.nest.refusedTitle'), { description: t('taskItem.subtaskTree.refused.depth') })
          setPendingSubtaskSelection(null)
          stopLanding()
          setDraggedIds(null)
          return
        }
        rememberUndo(
          () => source.restorePlacements(outcome.before),
          ids.length === 1
            ? t('undo.restored.nested', { title: first?.title ?? '' })
            : t('undo.restored.nestedMultiple', { count: ids.length }),
        )
        holdUntilGone(ids, droppedAt)
      })
      return
    }

    if (nest && !nest.blocked && tasks) {
      const movingIds = currentDraggedIds ?? new Set([String(active.id)])
      const ids = tasks.filter(task => movingIds.has(task.id)).sort((a, b) => a.order - b.order).map(task => task.id)
      const first = tasks.find(task => task.id === ids[0])
      const stopLanding = landGhost(document.querySelector('.drag-overlay-lift'), ids, id => `[data-subtask-id="${id}"]`)
      crossSegGapAfterRef.current = null
      setCrossSegGapAfter(null)
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      // Die gezogenen Zeilen bleiben zusammengeklappt, bis die Datenbank sie aus der Liste nimmt.
      setDraggedIds(movingIds)
      setDragCount(0)
      setActiveDragId(null)
      setPendingSubtaskSelection(ids)
      onExpandTask?.(nest.id)
      void source.nestTasks(ids, nest.id).then(outcome => {
        if ('refused' in outcome) {
          // Ein Ast, der unter der Aufgabe tiefer als vier Ebenen reichte (#181): sagen, warum nichts geschah.
          if (outcome.refused === 'depth') showInfoToast(t('taskList.nest.refusedTitle'), { description: t('taskItem.subtaskTree.refused.depth') })
          setPendingSubtaskSelection(null)
          stopLanding()
          setDraggedIds(null)
          return
        }
        rememberUndo(
          () => source.restorePlacements(outcome.before),
          ids.length === 1
            ? t('undo.restored.nested', { title: first?.title ?? '' })
            : t('undo.restored.nestedMultiple', { count: ids.length }),
        )
        holdUntilGone(ids, droppedAt)
      })
      return
    }

    if (!over || active.id === over.id || !tasks || !sections) {
      // Capture overlay's actual DOM position for FLIP settle animation.
      // Reading from the DOM is scroll-proof — initialRect + delta breaks
      // when auto-scroll shifts the viewport during drag.
      const overlayEl = document.querySelector('.drag-overlay-lift')
      if (overlayEl) {
        const overlayRect = overlayEl.getBoundingClientRect()
        const allIds = currentDraggedIds ? [...currentDraggedIds] : [String(active.id)]
        dropAnimRef.current = {
          ids: allIds,
          x: overlayRect.left,
          y: overlayRect.top,
        }
      }
      // No reorder — clear all state synchronously. With dropAnimation={null},
      // dnd-kit won't play a WAAPI animation, so no ghost issue.
      crossSegGapAfterRef.current = null
      setCrossSegGapAfter(null)
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      setDraggedIds(null)
      setDragCount(0)
      setActiveDragId(null)
      return
    }

    const activeId = String(active.id)
    const overId = String(over.id)

    // ── Drop on empty section zone ──
    if (overId.startsWith(EMPTY_SECTION_PREFIX)) {
      const targetSectionId = overId.slice(EMPTY_SECTION_PREFIX.length)
      const targetSeg = segments.find(seg => seg.section?.id === targetSectionId)
      if (!targetSeg?.section) {
        setIsCrossSegmentDrag(false)
        setGapAfterSegmentIdx(null)
        setDraggedIds(null)
        setDragCount(0)
        setActiveDragId(null)
        return
      }
      const sectionOrder = targetSeg.section.order
      const movingIds = currentDraggedIds ?? new Set([activeId])
      const movedTasks = tasks.filter(t => movingIds.has(t.id)).sort((a, b) => a.order - b.order)

      // Place moved tasks right after the section header's order value
      // Check if next segment's section needs bumping
      const targetSegIdx = segments.indexOf(targetSeg)
      const nextSegSection = segments[targetSegIdx + 1]?.section
      const nextBoundary = nextSegSection?.order
        ?? sectionOrder + movedTasks.length + 1

      const updates: Array<{ id: string; order: number; sectionId?: string | null }> = []
      let sectionBump: { id: string; order: number } | null = null
      const step = (nextBoundary - sectionOrder) / (movedTasks.length + 1)

      movedTasks.forEach((task, i) => {
        updates.push({
          id: task.id,
          order: sectionOrder + step * (i + 1),
          sectionId: targetSectionId,
        })
      })

      // Bump next section if orders would collide
      const maxOrder = sectionOrder + step * movedTasks.length
      if (nextSegSection && nextSegSection.order <= maxOrder + 0.001) {
        sectionBump = { id: nextSegSection.id, order: Math.ceil(maxOrder) + 2 }
      }

      // FLIP animation
      const overlayEl = document.querySelector('.drag-overlay-lift')
      if (overlayEl) {
        const overlayRect = overlayEl.getBoundingClientRect()
        dropAnimRef.current = {
          ids: [...movingIds],
          x: overlayRect.left,
          y: overlayRect.top,
        }
      }

      if (updates.length > 0) {
        const optimistic = new Map<string, { order: number; sectionId?: string | null }>()
        for (const u of updates) optimistic.set(u.id, u)
        setOptimisticUpdates(optimistic)
      }

      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      setDraggedIds(null)
      setDragCount(0)
      setActiveDragId(null)

      if (updates.length > 0) {
        const writeDb = async () => {
          if (sectionBump) {
            await source.updateSection(sectionBump.id, { order: sectionBump.order })
          }
          await source.reorderTasks(updates)
        }
        writeDb()
      }
      return
    }

    // Kurz notiert rechnet mit gedachten Abschnitten (nach Alter, `order` 0, keine Dexie-Zeile):
    // Ihre Kennung gehört nicht in die Aufgabe, ihre `order` ist keine Grenze (bis 28.09.2026
    // stand `__quick_notes_recent__` als `sectionId` in umsortierten Aufgaben).
    const segs = allowCrossSegmentDrag ? segments : segments.map(seg => ({ ...seg, section: null }))

    // Find target segment from the visual layout (order-based, not sectionId-based)
    const targetSegIdx = segs.findIndex(seg => seg.tasks.some(t => t.id === overId))
    if (targetSegIdx === -1) {
      setIsCrossSegmentDrag(false)
      setGapAfterSegmentIdx(null)
      setDraggedIds(null)
      setDragCount(0)
      setActiveDragId(null)
      return
    }
    const targetSeg = segs[targetSegIdx]
    const targetSectionId = targetSeg.section?.id ?? null
    const sectionOrder = targetSeg.section?.order ?? (targetSeg.tasks[0]?.order ?? 0) - 1

    const segTasks = targetSeg.tasks
    const movingIds = currentDraggedIds ?? new Set([activeId])

    // Remove moving items from segment tasks
    const remaining = segTasks.filter(t => !movingIds.has(t.id))

    // Find drop position
    const overIndex = remaining.findIndex(t => t.id === overId)
    const dropIndex = overIndex === -1 ? remaining.length : overIndex

    // Direction: insert before or after the over item.
    // Same-segment: standard direction check (dragging down = insert after).
    // Cross-segment from below: insert after (gap appears below the over item).
    const activeOrigIdx = segTasks.findIndex(t => t.id === activeId)
    const overOrigIdx = segTasks.findIndex(t => t.id === overId)
    const insertAfter = activeOrigIdx !== -1
      ? activeOrigIdx < overOrigIdx              // Same-segment: standard direction
      : (crossSegGapAfterRef.current ?? true)    // Cross-segment: from gap direction ref

    // Build new ordered list with moved items inserted
    const movedTasks = tasks.filter(t => movingIds.has(t.id)).sort((a, b) => a.order - b.order)
    const newOrder = [...remaining]
    const insertIdx = insertAfter ? dropIndex + 1 : dropIndex
    newOrder.splice(insertIdx, 0, ...movedTasks)

    // Upper bound from the next segment's section (or generous default)
    const nextSegSection = segs[targetSegIdx + 1]?.section
    const nextBoundary = nextSegSection?.order
      ?? (newOrder[newOrder.length - 1]?.order ?? sectionOrder) + newOrder.length + 1

    // Calculate new order values for moved tasks only
    const updates: Array<{ id: string; order: number; sectionId?: string | null }> = []
    let sectionBump: { id: string; order: number } | null = null
    const movedIndices = newOrder.reduce<number[]>((acc, t, i) => {
      if (movingIds.has(t.id)) acc.push(i)
      return acc
    }, [])

    if (movedIndices.length > 0) {
      const firstIdx = movedIndices[0]
      const lastIdx = movedIndices[movedIndices.length - 1]
      const prevOrder = firstIdx > 0 ? newOrder[firstIdx - 1].order : sectionOrder
      const nextOrder = lastIdx < newOrder.length - 1 ? newOrder[lastIdx + 1].order : nextBoundary

      if (prevOrder >= nextOrder) {
        // Degenerate case: renormalize entire segment
        newOrder.forEach((t, i) => {
          const newOrd = sectionOrder + i + 1
          const needsSectionUpdate = (t.sectionId ?? null) !== targetSectionId
          if (t.order !== newOrd || needsSectionUpdate) {
            updates.push({
              id: t.id,
              order: newOrd,
              ...(needsSectionUpdate ? { sectionId: targetSectionId } : {}),
            })
          }
        })
        const maxOrder = sectionOrder + newOrder.length
        if (nextSegSection && nextSegSection.order <= maxOrder + 1) {
          sectionBump = { id: nextSegSection.id, order: maxOrder + 2 }
        }
      } else {
        // Normal case: midpoint between neighbors
        const step = (nextOrder - prevOrder) / (movedIndices.length + 1)
        movedIndices.forEach((idx, i) => {
          const task = newOrder[idx]
          const needsSectionUpdate = (task.sectionId ?? null) !== targetSectionId
          updates.push({
            id: task.id,
            order: prevOrder + step * (i + 1),
            ...(needsSectionUpdate ? { sectionId: targetSectionId } : {}),
          })
        })
      }
    }

    // Capture overlay's actual DOM position for FLIP settle animation.
    // Reading from the DOM is scroll-proof — initialRect + delta breaks
    // when auto-scroll shifts the viewport during drag.
    const overlayEl = document.querySelector('.drag-overlay-lift')
    if (overlayEl) {
      const overlayRect = overlayEl.getBoundingClientRect()
      dropAnimRef.current = {
        ids: [...movingIds],
        x: overlayRect.left,
        y: overlayRect.top,
      }
    }

    // ── Synchronous state clearing ──
    // Set optimistic updates BEFORE clearing drag state so items appear at
    // their new positions in the same render as un-collapse.
    if (updates.length > 0) {
      const optimistic = new Map<string, { order: number; sectionId?: string | null }>()
      for (const u of updates) optimistic.set(u.id, u)
      setOptimisticUpdates(optimistic)
    }

    // Clear all drag state in one synchronous batch. React 18 batches these
    // with dnd-kit's internal reset into one render commit.
    // With dropAnimation={null}, there's no WAAPI animation — everything
    // happens in one frame: overlay unmounts, items un-collapse, active item visible.
    crossSegGapAfterRef.current = null
    setCrossSegGapAfter(null)
    setIsCrossSegmentDrag(false)
    setGapAfterSegmentIdx(null)
    setDraggedIds(null)
    setDragCount(0)
    setActiveDragId(null)

    // ── Async DB writes (fire-and-forget) ──
    // Visual order is already correct via optimistic updates. DB write + sync
    // happen asynchronously. useLiveQuery will catch up, then the useEffect
    // clears optimistic updates (after 100ms delay).
    if (updates.length > 0) {
      const writeDb = async () => {
        if (sectionBump) {
          await source.updateSection(sectionBump.id, { order: sectionBump.order })
        }
        await source.reorderTasks(updates)
      }
      writeDb()
    }
  }, [tasks, sections, segments, setOptimisticUpdates, source, nestTracker, onExpandTask, t, holdUntilGone, allowCrossSegmentDrag])

  const handleDragCancel = useCallback(() => {
    checklistSlotRef.current = null
    setInsertLine(null)
    nestTracker.reset()
    nestLatchRef.current = null
    lastOverIdRef.current = null
    draggedIdsRef.current = null
    pendingDragIdsRef.current = null
    crossSegGapAfterRef.current = null
    setCrossSegGapAfter(null)
    setIsCrossSegmentDrag(false)
    setGapAfterSegmentIdx(null)
    setActiveDragId(null)
    setDraggedIds(null)
    setDragCount(0)
  }, [nestTracker])

  /*
    **Herausziehen aus der Checkliste** (#204, Etappe 3). Die Checkliste meldet, sobald eine
    Zeile ihren Rand verlässt:
    - Auf der Mitte einer Aufgabe verweilen → umhängen (dieselbe Maschine wie beim Einhängen).
    - Sonst zeigt die Einfügelinie, wo sie als Aufgabe landet.
  */
  const lineSlotRef = useRef<LineSlot | null>(null)
  /** Läuft gerade ein Herausziehen? Für das Sicherheitsnetz unten. */
  const dragOutActiveRef = useRef(false)

  /*
    **Sicherheitsnetz für die Linie:** Nach jedem Loslassen — sobald weder die Liste noch die
    Checkliste zieht — steht keine Linie mehr da. Eine nachgereichte Bewegung von dnd-kit ließ
    sie einmal stehen (gemeldet 27.09.2026); was auch immer sie künftig neu zeichnet, bleibt
    nicht liegen.
  */
  useEffect(() => {
    if (!insertLine) return
    const onUp = () => {
      setTimeout(() => {
        if (draggedIdsRef.current || dragOutActiveRef.current) return
        checklistSlotRef.current = null
        lineSlotRef.current = null
        setInsertLine(null)
      }, 0)
    }
    document.addEventListener('pointerup', onUp)
    return () => document.removeEventListener('pointerup', onUp)
  }, [insertLine])

  const clearDragOut = useCallback(() => {
    dragOutActiveRef.current = false
    nestTracker.reset()
    nestExcludeRef.current = new Set()
    nestSourceRecurringRef.current = false
    lineSlotRef.current = null
    setInsertLine(null)
  }, [nestTracker])

  const subtaskDragOut = useMemo<SubtaskDragOut>(() => {
    const listRows = () => {
      const wrapper = taskListRef.current
      if (!wrapper) return []
      const ids = new Set(segmentsRef.current.flatMap(seg => seg.tasks).map(task => task.id))
      return [...wrapper.querySelectorAll<HTMLElement>('[data-task-id]')]
        .filter(el => ids.has(el.dataset.taskId ?? ''))
        .map(el => {
          const r = el.getBoundingClientRect()
          return { id: el.dataset.taskId!, top: r.top, bottom: r.bottom, left: r.left, right: r.right }
        })
    }
    return {
      move(pointer: Point, _subtaskIds: readonly string[], parentId: string, recurring = false) {
        dragOutActiveRef.current = true
        nestExcludeRef.current = new Set([parentId])
        nestSourceRecurringRef.current = recurring
        nestTracker.update(pointer)
        if (nestTracker.current && !nestTracker.current.blocked) {
          setInsertLine(null)
          return
        }
        const rows = listRows()
        const slot = lineAt(pointer, rows)
        if (!slot) return
        lineSlotRef.current = slot
        const row = rows.find(r => r.id === slot.rowId)!
        setInsertLine({ top: slot.before ? row.top - 1 : row.bottom + 1, left: row.left, width: row.right - row.left })
      },
      back() {
        clearDragOut()
      },
      drop(_pointer: Point, subtaskIds: readonly string[], parentId: string) {
        const target = nestTracker.current
        const slot = lineSlotRef.current
        clearDragOut()
        const ids = [...subtaskIds]
        if (ids.length === 0) return false
        // Mehrere (gewünscht 28.09.2026): eine Meldung mit der Zahl statt eines Titels.
        const title = source.getTask(ids[0]!).then(task => task?.title ?? '')
        const message = async (one: string, many: string) =>
          ids.length === 1 ? t(one, { title: await title }) : t(many, { count: ids.length })

        if (target && !target.blocked) {
          // **Umhängen**: Der Nutzer folgt der Unteraufgabe — das Ziel klappt auf, die Quelle zu.
          const stopLanding = landGhost(document.querySelector('.subtask-drag-overlay'), ids, id => `[data-subtask-id="${id}"]`)
          setPendingSubtaskSelection(ids)
          onExpandTask?.(target.id)
          void source.nestTasks(ids, target.id).then(async outcome => {
            if ('refused' in outcome) {
              // Ein Ast, der unter der Aufgabe tiefer als vier Ebenen reichte (#181): sagen, warum nichts geschah.
              if (outcome.refused === 'depth') showInfoToast(t('taskList.nest.refusedTitle'), { description: t('taskItem.subtaskTree.refused.depth') })
              setPendingSubtaskSelection(null)
              stopLanding()
              return
            }
            rememberUndo(async () => {
              await source.restorePlacements(outcome.before)
              // Der Blick kehrt mit zurück: Die Quelle klappt wieder auf, die Zeilen sind angefasst.
              setPendingSubtaskSelection(ids)
              onExpandTask?.(parentId)
            }, await message('undo.restored.moved', 'undo.restored.movedMultiple'))
          })
          return true
        }

        // Kurz notiert rechnet mit gedachten Abschnitten (nach Alter): Die zählen hier nicht.
        const segs = allowCrossSegmentDrag ? segmentsRef.current : segmentsRef.current.map(seg => ({ ...seg, section: null }))
        const at = slot && ordersAtLine(segs, slot, ids.length)
        if (!at) return false
        // Dort zählt das Anlegedatum: Sie übernimmt das der Zeile an der Linie und bleibt in deren Abschnitt.
        const createdAt = allowCrossSegmentDrag ? undefined : segs.flatMap(seg => seg.tasks).find(t => t.id === slot.rowId)?.createdAt
        const stopLanding = landGhost(document.querySelector('.subtask-drag-overlay'), ids, id => `[data-task-id="${id}"]`)
        // Vorgemerkt: Auswählen lassen sie sich erst, wenn sie in der Liste stehen.
        onSelectTasks?.(ids)
        void (async () => {
          const before: TaskPlacement[] = []
          for (const [i, id] of ids.entries()) {
            const placed = await source.unnestTask(id, { sectionId: at.sectionId, order: at.orders[i]!, createdAt })
            if (placed) before.push(...placed)
          }
          if (before.length === 0) {
            stopLanding()
            return
          }
          rememberUndo(() => source.restorePlacements(before), await message('undo.restored.unnested', 'undo.restored.unnestedMultiple'))
        })()
        return true
      },
      cancel() {
        clearDragOut()
      },
    }
  }, [nestTracker, clearDragOut, source, onExpandTask, onSelectTasks, t, taskListRef, allowCrossSegmentDrag])

  return {
    subtaskDragOut,
    insertLine,
    leaveList,
    activeDragId,
    draggedIds,
    dragCount,
    isCrossSegmentDrag,
    gapAfterSegmentIdx,
    gapHeight: gapHeightRef.current,
    crossSegGapAfterRef,
    crossSegGapAfter,
    nestTarget,
    nestLatchRef,
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  }
}
